import { useEffect, useState } from "react";
import { Link } from "react-router";
import { formatTenge, LOT_STATUS_LABELS, TRANSPORT_KIND_LABELS, vatLabel, type LotListItem, type LotStatus } from "@omts/shared";

/** Цвет бейджа статуса: торги — зелёный, этапы после торгов — синий, оплачено — серый, отмена — красный. */
export function statusBadgeClass(status: LotStatus): string {
  switch (status) {
    case "active":
      return "";
    case "awaiting_winner_data":
    case "formalized_pre_trip":
    case "delivered":
    case "closed":
      return "stage";
    case "cancelled":
    case "no_winner":
      return "danger";
    default:
      return "muted";
  }
}
import { api, subscribeEvents } from "../api";
import { PaymentTasks } from "../components/PaymentTasks";

const fmt = new Intl.DateTimeFormat("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

function timeLeft(deadlineIso: string): string {
  const ms = new Date(deadlineIso).getTime() - Date.now();
  if (ms <= 0) return "срок истёк";
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return h > 0 ? `${h} ч ${m} мин` : `${m} мин`;
}

export function LotsPage() {
  const [lots, setLots] = useState<LotListItem[]>([]);
  const [includeClosed, setIncludeClosed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = () =>
    api
      .lots(includeClosed)
      .then(setLots)
      .catch((e: Error) => setError(e.message));

  useEffect(() => {
    void load();
    // Любое изменение лота на сервере — перезагружаем список (SSE).
    const unsubscribe = subscribeEvents((type) => {
      if (type === "lot.updated") void load();
    });
    const tick = setInterval(() => setLots((l) => [...l]), 30_000); // обновить таймеры
    return () => {
      unsubscribe();
      clearInterval(tick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeClosed]);

  return (
    <>
      <div className="page-header">
        <h1>Заявки</h1>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <label className="muted" style={{ fontSize: 14 }}>
            <input type="checkbox" checked={includeClosed} onChange={(e) => setIncludeClosed(e.target.checked)} /> показать оплаченные и
            отменённые
          </label>
          <Link to="/lots/new" className="btn primary">
            + Новая заявка
          </Link>
        </div>
      </div>

      <PaymentTasks />

      {error && <p className="error">{error}</p>}
      {lots.length === 0 && !error && <p className="muted">Заявок пока нет.</p>}

      {lots.map((lot) => (
        <Link to={`/lots/${lot.id}`} key={lot.id} className="card lot-row">
          <div>
            <div style={{ fontWeight: 600 }}>{lot.number}</div>
            <div className="sub">{lot.projectName}</div>
          </div>
          <div>
            <div className="route">
              {lot.loadingAddress} → {lot.unloadingAddress}
            </div>
            <div className="sub">
              Погрузка {fmt.format(new Date(lot.loadingAt))} · {lot.vehicleTypeName} · {lot.weightKg.toLocaleString("ru-RU")} кг ·{" "}
              {TRANSPORT_KIND_LABELS[lot.transportKind]}
            </div>
          </div>
          <div>
            {lot.currentMinAmount === null ? (
              <div className="price empty">ставок нет</div>
            ) : (
              <div className="price">{formatTenge(lot.currentMinAmount)}</div>
            )}
            <div className="sub">
              {lot.bidsCount} ставок · {vatLabel(lot.transportKind)}
            </div>
          </div>
          <div>
            <span className={`badge ${statusBadgeClass(lot.status)}`}>{LOT_STATUS_LABELS[lot.status]}</span>
            {lot.status === "active" && (
              <div className="sub">
                до {fmt.format(new Date(lot.deadlineAt))} · {timeLeft(lot.deadlineAt)}
                {lot.autoExtend && ` · автопродление ${lot.autoExtensionsUsed}/2`}
              </div>
            )}
          </div>
          <div className="sub">{lot.createdByName}</div>
        </Link>
      ))}
    </>
  );
}
